import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Inbox, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";
import { EmailList } from "@/components/mailbox/EmailList";
import { MailboxRules } from "@/components/mailbox/MailboxRules";
import { ScrollFade } from "@/components/ScrollFade";
import { TopicsManager } from "@/components/mailbox/TopicsManager";
import { Settings2 } from "lucide-react";
import { useMailboxBadge } from "@/components/mailbox/useMailboxBadge";

const VIEWS = [["list", "Liste"], ["target", "Nach Kunde/Hersteller"], ["topic", "Nach Thema"]];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "noch nie");

export default function Mailbox() {
  const { user } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("pending");
  const badge = useMailboxBadge();
  const [listKey, setListKey] = useState(0);
  const [view, setView] = useState(() => localStorage.getItem("mailbox-view") || "list");
  const [topicsOpen, setTopicsOpen] = useState(false);

  const loadStatus = useCallback(() => mailboxApi.status().then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus, listKey]);

  const syncNow = async () => {
    await mailboxApi.syncNow();
    toast.success("Abruf gestartet – neue Mails erscheinen in Kürze");
    setTimeout(() => { loadStatus(); setListKey((k) => k + 1); }, 8000);
  };

  const c = status?.counts || {};
  const showViews = tab !== "ignored" && tab !== "rules";
  const changeView = (v) => { setView(v); localStorage.setItem("mailbox-view", v); };
  return (
    <div className="space-y-6 animate-fade-in" data-testid="mailbox-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Inbox className="w-8 h-8" />Service-Postfach</h1>
          <p className="text-muted-foreground mt-1">{status?.mailbox || "service@r-network.de"} · Abruf alle 5 Minuten (Posteingang und Gesendet)</p>
        </div>
        {user?.role === "admin" && <Button variant="outline" onClick={syncNow} data-testid="mailbox-sync-btn"><RefreshCw className="w-4 h-4 mr-2" />Jetzt abrufen</Button>}
      </div>

      {status && (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm rounded-lg border px-3 py-2 ${status.last_error ? "border-amber-300 bg-amber-50 text-amber-800" : "text-muted-foreground"}`} data-testid="mailbox-status">
          {status.last_error ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />}
          {status.last_error ? `${status.last_error} (letzter Versuch ${fmt(status.last_attempt_at)})` : `Zuletzt abgerufen: ${fmt(status.last_sync_at)}`}
          <span className="w-full sm:w-auto sm:ml-auto text-xs">Gespeichert werden nur Absender, Empfänger, Betreff, Datum und ein kurzer Textauszug – keine Anhänge.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <ScrollFade>
          <TabsList>
            <TabsTrigger value="pending" data-testid="mailbox-tab-pending">Unbearbeitet ({badge.pending}{badge.unassigned ? `, ${badge.unassigned} ohne Zuordnung` : ""})</TabsTrigger>
            <TabsTrigger value="open" data-testid="mailbox-tab-open">Zuordnung offen ({c.open ?? "…"})</TabsTrigger>
            <TabsTrigger value="assigned" data-testid="mailbox-tab-assigned">Zugeordnet ({c.assigned ?? "…"})</TabsTrigger>
            <TabsTrigger value="ignored" data-testid="mailbox-tab-ignored">Ignoriert ({c.ignored ?? "…"})</TabsTrigger>
            <TabsTrigger value="rules" data-testid="mailbox-tab-rules">Regeln</TabsTrigger>
          </TabsList>
        </ScrollFade>
        {showViews && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40" role="group" data-testid="mailbox-view-toggle">
              {VIEWS.map(([v, label]) => (
                <button key={v} onClick={() => changeView(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === v ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`} data-testid={`mailbox-view-${v}`}>{label}</button>
              ))}
            </div>
            {view === "topic" && user?.role === "admin" && (
              <Button variant="ghost" size="sm" onClick={() => setTopicsOpen(true)} data-testid="mailbox-topics-manage"><Settings2 className="w-4 h-4 mr-1.5" />Themen verwalten</Button>
            )}
          </div>
        )}
        {topicsOpen && <TopicsManager open={topicsOpen} onOpenChange={setTopicsOpen} onChanged={() => setTimeout(() => setListKey((k) => k + 1), 4000)} />}
        <TabsContent value="rules">{tab === "rules" && <MailboxRules />}</TabsContent>
        {["pending", "open", "assigned", "ignored"].map((s) => (
          <TabsContent key={s} value={s}>
            {tab === s && (
              <EmailList key={`${s}-${listKey}`} params={s === "pending" ? { work: "pending" } : { status: s }} showTarget testId={`mailbox-list-${s}`} onLoaded={loadStatus} view={s === "ignored" ? "list" : view}
                emptyText={s === "pending" ? "Alles bearbeitet – keine offenen Mails im Posteingang." : s === "open" ? "Alles zugeordnet – keine offenen E-Mails." : s === "assigned" ? "Noch keine zugeordneten E-Mails." : "Keine ignorierten E-Mails."} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
